import React from 'react';
import { Link } from 'react-router-dom';
import ImplantCenterLayout from './ImplantCenterLayout';

const MiniDentalImplants = () => {
  return (
    <ImplantCenterLayout
      title="Mini Dental Implants Fort Worth TX | SDI Implants | Fort Worth Dental Implant Center"
      description="Mini dental implants (SDI) in Fort Worth TX. Smaller diameter implants ideal for patients with limited bone. Less invasive, faster healing. FREE consultation. Call (817) 854-4329."
      keywords="mini dental implants fort worth, SDI implants, small diameter implants, mini implants fort worth tx, denture stabilization implants"
    >
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{backgroundImage: "url('/images/services/dental-implant-model.jpg')"}} />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Mini Dental Implants in Fort Worth, TX
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              A less invasive alternative for patients with limited jawbone density. Smaller diameter implants with faster healing times.
            </p>
            <a href="tel:8178544329" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition inline-block">
              FREE Consultation: (817) 854-4329
            </a>
          </div>
        </div>
      </section>

      {/* What Are Mini Implants */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">What Are Mini Dental Implants?</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                <strong>Mini dental implants</strong> (also called Small Diameter Implants or SDI) are narrower than traditional implants, typically measuring 1.8mm to 3.3mm in diameter compared to 3.5mm to 6mm for standard implants.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                These smaller implants are an excellent option for patients who:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Have limited jawbone density</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Want to stabilize loose dentures</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Prefer a less invasive procedure</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Need a more affordable implant option</li>
              </ul>
            </div>
            <div>
              <img 
                src="/images/services/mini-implants-diagram.jpg" 
                alt="Mini dental implants comparison diagram Fort Worth TX" 
                className="rounded-xl shadow-xl w-full"
                onError={(e) => { e.target.src = '/images/services/dental-implant-model.jpg'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Benefits of Mini Dental Implants</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Less Invasive</h3>
              <p className="text-gray-600">Smaller incision, less tissue disruption, and often no sutures required.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="text-4xl mb-4">⏱️</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Faster Recovery</h3>
              <p className="text-gray-600">Most patients resume normal activities the same day with minimal discomfort.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">More Affordable</h3>
              <p className="text-gray-600">Lower cost than traditional implants while still providing excellent results.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="text-4xl mb-4">🦴</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Bone Graft Needed</h3>
              <p className="text-gray-600">Often suitable for patients with insufficient bone for standard implants.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini vs Standard */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Mini vs. Standard Dental Implants</h2>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-teal-600 text-white">
                  <tr>
                    <th className="py-4 px-6 text-left">Feature</th>
                    <th className="py-4 px-6 text-left">Mini Implants</th>
                    <th className="py-4 px-6 text-left">Standard Implants</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr><td className="py-4 px-6 font-medium">Diameter</td><td className="py-4 px-6">1.8mm - 3.3mm</td><td className="py-4 px-6">3.5mm - 6mm</td></tr>
                  <tr className="bg-gray-50"><td className="py-4 px-6 font-medium">Surgery</td><td className="py-4 px-6">Minimally invasive</td><td className="py-4 px-6">Standard surgery</td></tr>
                  <tr><td className="py-4 px-6 font-medium">Healing Time</td><td className="py-4 px-6">Same day to few weeks</td><td className="py-4 px-6">3-6 months</td></tr>
                  <tr className="bg-gray-50"><td className="py-4 px-6 font-medium">Bone Graft</td><td className="py-4 px-6">Usually not needed</td><td className="py-4 px-6">Often required</td></tr>
                  <tr><td className="py-4 px-6 font-medium">Cost</td><td className="py-4 px-6">Lower</td><td className="py-4 px-6">Higher</td></tr>
                  <tr className="bg-gray-50"><td className="py-4 px-6 font-medium">Best For</td><td className="py-4 px-6">Denture stabilization, limited bone</td><td className="py-4 px-6">Single tooth, bridges</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Common Uses */}
      <section className="py-16 bg-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Common Uses for Mini Dental Implants</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Denture Stabilization</h3>
                <p className="text-gray-700">The most common use for mini implants. They provide a secure anchor for loose dentures, eliminating slipping and improving chewing ability.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Small Teeth Replacement</h3>
                <p className="text-gray-700">Ideal for replacing smaller teeth like lower front teeth where space is limited.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Narrow Bone Areas</h3>
                <p className="text-gray-700">Perfect for areas where bone width is insufficient for standard implants without grafting.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Temporary Anchorage</h3>
                <p className="text-gray-700">Used as temporary anchors during orthodontic treatment to facilitate tooth movement.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Are Mini Dental Implants Right for You?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Schedule your FREE consultation to find out if mini implants are the best solution for your needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8178544329" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition">
              Call (817) 854-4329
            </a>
            <Link to="/implant-center#consultation-form" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-500 transition">
              Request Consultation
            </Link>
          </div>
        </div>
      </section>
    </ImplantCenterLayout>
  );
};

export default MiniDentalImplants;
